/* eslint-disable @next/next/no-img-element */
import { DOMParser } from "xmldom";
import { getCMSImageURL } from "./CMSImageURL";
import axiosInstance from "@/config/axiosInstance";
import { getBillingApiRoute } from "./api.util";
import ForAdultsIcon from "./svg/forAdultsIcon.svg";
import { ALL_AGES_CONTENT_AGE_RANGE } from "./ageRange.utils";
import { getServerNow } from "@/services/publicAuthConfigService";
import { getArtistPriority } from "./artistPriority.utils";

export function divideIntoSubarrays(arr, numSubarrays = 4) {
  try {
    const subarrays = Array.from({ length: numSubarrays }, () => []); // Create empty subarrays

    arr.forEach((item, index) => {
      subarrays[index % numSubarrays].push(item); // Distribute items round-robin
    });

    return subarrays;
  } catch (error) {
    console.log(arr);
    return [];
  }
}

export function parseVastXML(vastXml) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(vastXml, "text/xml");
    // Get the creatives
    const creatives = doc.getElementsByTagName("Creative");
    const creativesInfo = [];

    for (let i = 0; i < creatives.length; i++) {
      const creative = creatives[i];
      const creativeId = creative.getAttribute("id");
      const creativeSlot = creative.getAttribute("creative-slot");
      const creativeCapacity = +creative.getAttribute("ext-capacity");
      const creativeRandomMedia = Boolean(
        creative.getAttribute("ext-random-media")
      );

      const linear = creative.getElementsByTagName("Linear")[0];
      const duration =
        linear?.getElementsByTagName("Duration")[0]?.textContent || null;

      const mediaFiles = linear?.getElementsByTagName("MediaFile");
      const mediaUrls = [];

      for (let j = 0; j < mediaFiles?.length; j++) {
        mediaUrls.push(mediaFiles[j]?.textContent);
      }

      const clickthrough =
        linear?.getElementsByTagName("ClickThrough")[0]?.textContent || null;
      const clickTracking =
        linear?.getElementsByTagName("ClickTracking")[0]?.textContent || null;

      const trackingEvents = {};
      const trackingNodes = linear?.getElementsByTagName("Tracking");

      for (let k = 0; k < trackingNodes?.length; k++) {
        const event = trackingNodes[k]?.getAttribute("event");
        const eventUrl = trackingNodes[k]?.textContent;
        trackingEvents[event] = eventUrl;
      }

      creativesInfo.push({
        creativeId,
        creativeCapacity,
        creativeRandomMedia,
        creativeSlot,
        duration,
        mediaUrls,
        clickthrough,
        clickTracking,
        trackingEvents,
      });
    }

    return {
      preRoll: creativesInfo.find((x) => x.creativeSlot === "pre-roll") || {},
      firstQuartile:
        creativesInfo.find((x) => x.creativeSlot === "first-quartile") || {},
      midRoll: creativesInfo.find((x) => x.creativeSlot === "mid-roll") || {},
      thirdQuartile:
        creativesInfo.find((x) => x.creativeSlot === "third-quartile") || {},
    };
  } catch (error) {
    console.log(`VAST Parsing Error: ${error}`);
    return null;
  }
}

/**
 * Fetch VAST JSON data server-side (for SSR in page.jsx)
 * The endpoint remains public, but forwarding the optional user token lets the
 * advertisement service attach the authenticated phone to analytics.
 *
 * @param {string} contentKey - Content slug
 * @param {string} [campaignKey] - Optional campaign slug to force
 * @param {{deviceId?: string, isp?: string, mobileOperator?: string, accessToken?: string}} [requestContext]
 * @returns {Promise<object|null>} Transformed VAST data or null
 */
export async function fetchVastJsonSSR(
  contentKey,
  campaignKey,
  requestContext
) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8_000);

  try {
    const AD_API_BASE =
      process.env.AD_API_URL || "https://advertisement-api.manabakh.ir";
    const query = new URLSearchParams({ contentKey });
    query.set("planningOnly", "true");
    if (campaignKey) query.set("campaignKey", campaignKey);
    if (requestContext?.deviceId) {
      query.set("deviceId", requestContext.deviceId);
    }
    if (requestContext?.isp) query.set("isp", requestContext.isp);
    if (requestContext?.mobileOperator) {
      query.set("mobileOperator", requestContext.mobileOperator);
    }
    const url = `${AD_API_BASE}/v1/advertisement/api/campaign/gen-active-vast-json?${query}`;

    const response = await fetch(url, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      ...(requestContext?.accessToken
        ? {
            headers: {
              Authorization: `Bearer ${requestContext.accessToken}`,
            },
          }
        : {}),
      signal: controller.signal,
    });

    if (response.status === 204) {
      return null;
    }

    if (!response.ok) {
      console.log("[fetchVastJsonSSR] Non-OK response:", response.status);
      return null;
    }

    const json = await response.json();
    const vastData = json?.data ?? null;
    return transformVastJsonToPlayerData(vastData);
  } catch (err) {
    if (err?.name !== "AbortError") {
      console.log("[fetchVastJsonSSR] Error:", err);
    }
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Transform VAST JSON API response into the player's expected format.
 * Provides both the new creatives array (with full tracking) and
 * legacy compat fields (preRoll, firstQuartile, midRoll, thirdQuartile).
 *
 * @param {object} vastJson - Raw VAST JSON data from the API
 * @returns {object|null} Normalized VAST data for the player
 */
export function transformVastJsonToPlayerData(vastJson) {
  if (!vastJson || !vastJson.ad || !vastJson.ad.inline) return null;

  const { ad } = vastJson;
  const inline = ad.inline;
  const creatives = inline.creatives || [];
  const impressionUrls = Array.isArray(inline.impressions)
    ? inline.impressions.filter(
        (url) => typeof url === "string" && url.trim().length > 0
      )
    : [];

  if (impressionUrls.length === 0 && inline.impression) {
    impressionUrls.push(inline.impression);
  }

  // Build legacy compat fields from the creatives
  const slotMap = {};
  creatives.forEach((creative) => {
    const slot = creative.creativeSlot;
    if (!slot) return;

    const mediaUrls = (creative.linear?.mediaFiles || []).map((mf) => mf.url);
    const legacyEntry = {
      creativeId: creative.id,
      creative,
      creativeCapacity: parseInt(creative.extCapacity, 10) || mediaUrls.length,
      creativeRandomMedia: !!creative.extRandomMedia,
      creativeSlot: slot,
      duration: creative.linear?.duration || null,
      mediaUrls,
      clickthrough: creative.linear?.videoClicks?.clickThrough || null,
      clickTracking: creative.linear?.videoClicks?.clickTracking || null,
      trackingEvents: (creative.linear?.trackingEvents || []).reduce(
        (acc, te) => {
          acc[te.event] = te.url;
          return acc;
        },
        {}
      ),
    };

    // Support multiple creatives per slot (teaser groups) —
    // use the first one encountered for legacy compat
    const legacySlotName =
      slot === "pre-roll"
        ? "preRoll"
        : slot === "first-quartile"
        ? "firstQuartile"
        : slot === "mid-roll"
        ? "midRoll"
        : slot === "third-quartile"
        ? "thirdQuartile"
        : null;

    if (legacySlotName && !slotMap[legacySlotName]) {
      slotMap[legacySlotName] = legacyEntry;
    }
  });

  return {
    // New enriched data
    creatives,
    impressionUrls,
    impressionUrl: impressionUrls[0] || null,
    adId: ad.id || null,
    adTitle: inline.adTitle || null,
    campaignKey:
      vastJson.campaignKey || vastJson.campaignSlug || ad.campaignKey || null,
    // Legacy compat
    preRoll: slotMap.preRoll || {},
    firstQuartile: slotMap.firstQuartile || {},
    midRoll: slotMap.midRoll || {},
    thirdQuartile: slotMap.thirdQuartile || {},
  };
}

export const getImageForType = (type = "", contentPhotos = [], device) => {
  const targetSrc =
    contentPhotos?.find((x) => x.type === type && x.device === device)?.src ||
    contentPhotos?.find((x) => x.type === type)?.src ||
    contentPhotos?.find((x) => x.type === "poster")?.src;

  if (targetSrc) return getCMSImageURL(targetSrc);

  return null;
};

export const mergeArtistDuplicatesPlain = (arr) => {
  const nameMap = new Map(); // To track unique farsiName and corresponding properties

  arr.forEach((obj) => {
    const { farsiName, profession, contentPhotos, id, slug } = obj;

    if (nameMap.has(farsiName)) {
      // If farsiName already exists, push the profession and merge contentPhotos
      const existing = nameMap.get(farsiName);

      // Avoid duplicating professions
      if (!existing.profession.includes(profession.trim())) {
        existing.profession += `, ${profession.trim()}`;
      }

      // Merge contentPhotos (you might want to prevent duplicate photos if needed)
      existing.contentPhotos = [...existing.contentPhotos, ...contentPhotos];
    } else {
      // If farsiName doesn't exist, add a new entry
      nameMap.set(farsiName, {
        farsiName: farsiName,
        profession: profession.trim(),
        contentPhotos: [...contentPhotos], // Copy the contentPhotos array
        id: id, // Keep the original ID (or decide on other logic if merging IDs)
        slug: slug,
      });
    }
  });

  // Convert the map back to an array
  return Array.from(nameMap.values());
};

export const mergeArtistDuplicates = (arr) => {
  const nameProfessionMap = new Map(); // To track unique farsiName and professions

  arr.forEach((item) => {
    const artist = item.artist;
    const { farsiName, profession, contentPhotos, id, slug } = artist;
    const priority = getArtistPriority(item);
    const professionTrimmed = profession.trim(); // Trim spaces from profession

    if (nameProfessionMap.has(farsiName)) {
      const existing = nameProfessionMap.get(farsiName);
      if (
        priority !== null &&
        (existing.priority === null || priority > existing.priority)
      ) {
        existing.priority = priority;
      }

      // Check if the profession is already associated with this farsiName
      if (!existing.professions.includes(professionTrimmed)) {
        // Add new profession if not already present
        existing.professions.push(professionTrimmed);
        existing.profession = existing.professions.join(", ");
        // Merge contentPhotos (you may prevent duplicate photos as needed)
        existing.contentPhotos = [...existing.contentPhotos, ...contentPhotos];
      }
    } else {
      // If farsiName doesn't exist, add a new entry with the profession
      nameProfessionMap.set(farsiName, {
        farsiName: farsiName,
        profession: professionTrimmed,
        professions: [professionTrimmed], // Track professions as an array
        contentPhotos: [...contentPhotos], // Copy the contentPhotos array
        id: id, // Keep the original ID
        slug: slug,
        priority,
      });
    }
  });

  // Convert the map back to an array and structure it the same way as original
  return Array.from(nameProfessionMap.values()).map((artist) => ({ artist }));
};

export function maskMobileNumber(mobileNumber) {
  // Convert number to string to handle cases when number is passed as an integer
  const strNumber = String(mobileNumber);

  // Ensure the number has at least 10 characters to avoid masking incorrectly
  if (strNumber?.length < 10) {
    return null;
  }

  // Leave the first 3 and last 4 digits visible, mask the rest with '*'
  const visibleStart = strNumber.slice(0, 3);
  const visibleEnd = strNumber.slice(-4);
  const maskedSection = "*".repeat(strNumber?.length - 7);

  // Return the masked mobile number
  return `${visibleEnd}${maskedSection}${visibleStart}`;
}

export function base64EncodeWithoutPadding(input) {
  const base64 = btoa(input);
  return base64.replace(/=+$/, ""); // Remove trailing '=' characters
}

export function base64DecodeWithoutPadding(base64) {
  const paddedBase64 = base64.padEnd(
    base64?.length + ((4 - (base64?.length % 4)) % 4),
    "="
  );
  return atob(paddedBase64);
}

export const getUserSubscriptionStatus = async () => {
  try {
    const response = await axiosInstance.get(getBillingApiRoute("payment"), {
      params: {
        pageIndex: 10,
        pageNumber: 1,
        status: 1,
        orderType: "subscription",
      },
    });
    const { data } = response;

    const activeItem = data?.data?.results.find(
      (item) =>
        item.status === 1 &&
        item.subscriptionInfo &&
        new Date(item.subscriptionInfo.expireDate).getTime() > getServerNow()
    );

    if (!activeItem) {
      return 0;
    }

    const expireDate = new Date(activeItem.subscriptionInfo.expireDate);
    const timeDiff = expireDate.getTime() - getServerNow();
    const daysDiff = Math.round(timeDiff / (1000 * 60 * 60 * 24));

    return daysDiff;
  } catch (err) {
    console.log("Payment Status get error", err);
    return 0;
  }
};

export const getAgeRangeLabel = (ageRange) => {
  switch (Number(ageRange)) {
    case ALL_AGES_CONTENT_AGE_RANGE:
      return (
        <div className="flex flex-row justify-center items-center z-10 py-2 md:py-0">
          <span
            className="text-[10px] md:text-[11px] lg:text-sm"
            style={{ color: "#FDC13C" }}
          >
            مناسب تمامی سنین
          </span>
        </div>
      );
    case 120:
      return (
        <div className="flex flex-row justify-center items-center z-10 py-2 md:py-0">
          <ForAdultsIcon />
          <span className="text-[11px] md:text-xs lg:text-lg text-[#FDC13C] mr-2">
            مناسب بالای ۱۸ سال
          </span>
        </div>
      );
    case 18:
      return (
        <div className="flex flex-row justify-center items-center z-10 py-2 md:py-0">
          <img src="/images/13+.svg" width={24} alt="13+" />
          <span className="text-[11px] md:text-xs lg:text-lg text-[#FDC13C] mr-2">
            مناسب بالای ۱۳ سال
          </span>
        </div>
      );
    case 13:
      return (
        <div className="flex flex-row justify-center items-center z-10 py-2 md:py-0">
          <img src="/images/7+.svg" width={24} alt="13+" />
          <span className="text-[11px] md:text-xs lg:text-lg text-[#FDC13C] mr-2">
            مناسب بالای ۷ سال
          </span>
        </div>
      );
    case 7:
      return (
        <div className="flex flex-row justify-center items-center z-10 py-2 md:py-0">
          <img src="/images/7.svg" width={24} alt="13+" />
          <span className="text-[11px] md:text-xs lg:text-lg text-[#FDC13C] mr-2">
            مناسب زیر ۷ سال
          </span>
        </div>
      );

    default:
      return (
        <div className="flex flex-row justify-center items-center z-10 py-2 md:py-0">
          <ForAdultsIcon />
          <span className="text-[11px] md:text-xs lg:text-lg text-[#FDC13C] mr-2">
            مناسب برای بالای ۱۸ سال
          </span>
        </div>
      );
  }
};
